// src/pages/Team/Faces.jsx
// STATUS: active | UPDATED: 2026-09-26
// LOG:
//   2026-09-26  New. Tyler: the owner sees his sales managers, clicks one and
//               gets their view. He sees the sales floor as avatars, clicks one
//               and gets their account.
//
// THE FLOOR AS FACES, WHICH IS A DIFFERENT QUESTION FROM THE ROSTER.
//
// The roster under this is a list of rows sorted by email, which is the right
// shape for looking somebody up and the wrong shape for looking at everybody.
// An owner walking in does not have a name in mind. He has a store in mind, and
// what he wants is the same thing he gets standing at the back of the showroom,
// which is everyone at once and whoever needs him first.
//
// TWO ROWS BECAUSE THEY ARE TWO QUESTIONS.
//
// "Are my managers running their floors" and "is anybody stuck" do not get
// answered by the same faces, and an owner asks the first one before the
// second. A manager sees only the floor row, because his own peers are not his
// to inspect.
//
// THE RING IS THE ONLY THING COLOURED, and only on somebody waiting for a
// signature. That is the one state where a person has done their part and is
// held up by ours, which is the only thing on this screen that goes stale by
// being ignored.
//
// It scrolls sideways on a phone rather than wrapping into a wall of circles.
// Twelve faces wrap to four rows on a 375px screen and push the roster off the
// bottom, and a floor is a row of people, not a grid of them.

import { Box, HStack, Text } from '@chakra-ui/react'
import PersonAvatar from './PersonAvatar.jsx'

function Row({ label, lead, people, waitingIds }) {
  if (people.length === 0) return null
  return (
    <Box>
      <Text textStyle="eyebrow" color="inkDim" mb={1}>{label}</Text>
      <Text fontSize="body-sm" color="inkMuted" mb={3}>{lead}</Text>
      <HStack
        spacing={1}
        overflowX="auto"
        pb={1}
        // The rail owns its own overflow. The page never moves sideways.
        sx={{
          scrollSnapType: 'x proximity',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
          '& > *': { scrollSnapAlign: 'start' },
        }}
      >
        {people.map((p) => (
          <PersonAvatar
            key={p.id}
            person={p}
            to={`/team/${p.id}/`}
            state={waitingIds.has(p.id) ? 'waiting' : 'none'}
            sub={waitingIds.has(p.id) ? 'waiting on you' : undefined}
          />
        ))}
      </HStack>
    </Box>
  )
}

export default function Faces({ members, role, waitingIds }) {
  const waiting = waitingIds || new Set()
  const managers = members.filter((m) => m.role === 'manager')
  const floor = members.filter((m) => m.role === 'employee')

  if (managers.length === 0 && floor.length === 0) return null

  return (
    <Box bg="card" border="1px solid" borderColor="line" borderRadius="cardLg" p={{ base: 5, md: 6 }}>
      <Box display="flex" flexDirection="column" gap={7}>
        {role === 'owner' && (
          <Row
            label="YOUR SALES MANAGERS"
            lead={
              managers.length === 1
                ? 'Tap to see their week and their floor.'
                : 'Tap anybody to see their week and their floor.'
            }
            people={managers}
            waitingIds={waiting}
          />
        )}
        <Row
          label="THE FLOOR"
          lead={
            waiting.size > 0
              ? `${waiting.size} ${waiting.size === 1 ? 'person is' : 'people are'} waiting on a signature.`
              : 'Tap anybody to see where they are in the thirteen steps.'
          }
          people={floor}
          waitingIds={waiting}
        />
      </Box>
    </Box>
  )
}
